import { axs } from 'api/config/axiosConfig';
import { fetchProjectFileDownloadUrl } from 'api/file';
import T from 'components/common/text/T';
import ProjectFile from 'components/project/ProjectFile';
import { handleClearPmsData, pmsViewThumbnail } from 'containers/base/PmsConnect';
import { CloudFile, LocalFile, ProjectContext } from 'contexts/project/ProjectContext';
import useCheckSetInput from 'lib/hooks/useCheckSetInput';
import useInput from 'lib/hooks/useInput';
import useModel from 'lib/hooks/useModel';
import useMultiFileInput from 'lib/hooks/useMultiFileInput';
import { getFileDataUrl } from 'lib/library';
import { projectEventType, scanFileExtionsion } from 'lib/mapper';
import { useDidUpdateEffect } from 'lib/utils';
import _, { debounce } from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import uuid from 'react-uuid';
import { AppActions, FileActions, PartnerActions, ProjectActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import { downloadFile, downloadFileWithElectron } from 'store/utils';

const imgTypes = ['png', 'jpg', 'jpeg', 'gif'];
const modelTypes = ['obj', 'ply', 'stl'];
const scanfileTypes = ['.obj', '.ply', '.stl'];

interface ProjectFileContainerProps {
  hasViewer: boolean;
}

// detailPage에서 hasViewser true 로 호출
function ProjectFileContainer({ hasViewer = false }: ProjectFileContainerProps) {
  const {
    user,
    projectInfo,
    syncPartnersData,
    syncPartnersSuccess,
    uploadProjectFileSuccess,
    hasAlarm,
    editProjectShareSuccess,
    createProjectSyncSuccess,
    isPmsReady,
    isPmsOrder,
    pmsCommand,
    pmsData,
  } = useShallowAppSelector(state => ({
    user: state.user.user,
    projectInfo: state.project.project.data?.projectInfo,
    syncPartnersData: state.partner.syncPartners.data,
    syncPartnersSuccess: state.partner.syncPartners.success,
    uploadProjectFileSuccess: state.file.uploadProjectFile.success,
    hasAlarm: state.project.hasAlarm,
    editProjectShareSuccess: state.file.editProjectShare.success,
    createProjectSyncSuccess: state.project.createProjectSync.success,
    isPmsReady: state.pms.isPmsReady,
    isPmsOrder: state.pms.isPmsOrder,
    pmsCommand: state.pms.command,
    pmsData: state.pms.pmsData,
  }));
  const {
    partnerFileListCt,
    cloudFileListCt,
    scanFileListCt,
    localFileListCt,
    onLoadLocalFiles,
    viewColors,
    onCheckExistingUploadUserFile,
    onUploadLocalFile,
    localFileToUpload,
  } = useContext(ProjectContext);
  const { pcode } = useParams() as { pcode: string };
  const { ipcRenderer } = window;
  const syncInfo = projectInfo?.syncInfo;
  const [selectedModelFile, setSelectedModelFile] = useState<any>();
  const isModelFileLoadingProgress = useInput(false);
  const [isSelectedPartner, setIsSelectedPartner] = useState(false);
  const projectId = projectInfo?.projectId;
  const projectCode = pcode;
  const company = user?.company;
  const [syncPartners, setSyncPartners] = useState([]);
  const syncPartnersPagingData = syncPartnersData?.pagingData;
  const hasMoreSyncPartners = syncPartnersPagingData?.page < syncPartnersPagingData?.totalPage;
  const htmlViewerRef = useRef<HTMLIFrameElement | null>(null);
  const checkVisibleModelFile = useCheckSetInput(new Set([]));
  const syncPartnerKeyword = useInput('');
  const selectedSyncPartner = useInput('');
  const viewModelFile = useInput([]);
  const viewHtmlFile = useInput(null);
  const viewImageFile = useInput(null);
  const localFileInput = useMultiFileInput([]);
  const localFileInputRef = useRef<HTMLDivElement | any>(null);
  const partnerFileList = useInput([]);
  const cloudFileList = useInput([]);
  const [isRequestedSync, setIsRequestedSync] = useState(false);
  const setSyncPartnersValue = useMemo(() => {
    return syncPartnersSuccess && setSyncPartners(draft => draft.concat(syncPartnersData?.list));
  }, [syncPartnersSuccess]);
  const setIsRequestedSyncValue = useMemo(() => {
    return syncInfo?.syncStatus === 0 && setIsRequestedSync(true);
  }, [syncInfo?.syncStatus]);
  const [isCheckedCloud, setIsCheckedCloud] = useState<boolean>(false);
  const [isCheckedLocal, setIsCheckedLocal] = useState<boolean>(false);
  const { addWireFrame, removeWireFrame } = useModel();

  useEffect(() => {
    setIsCheckedCloud(!!cloudFileList.value.find((item: any) => !!item.isChecked));
  }, [cloudFileList.value]);

  useEffect(() => {
    setIsCheckedLocal(!!localFileListCt.value.find((item: any) => !!item.isChecked));
  }, [localFileListCt.value]);

  useDidUpdateEffect(() => {
    if (createProjectSyncSuccess) {
      setIsSelectedPartner(true);
    }
    if (editProjectShareSuccess || createProjectSyncSuccess) {
      FileActions.fetch_project_files_request(projectCode);
    }
  }, [!!editProjectShareSuccess, !!createProjectSyncSuccess]);

  const handleFetchSyncPartners = debounce((first?: boolean) => {
    if (first) return PartnerActions.fetch_sync_partners_request({ keyword: '', page: 1 });

    const { page, totalPage } = syncPartnersPagingData;
    if (page >= totalPage) return;
    PartnerActions.fetch_sync_partners_request({
      keyword: syncPartnerKeyword.value || '',
      page: page + 1,
    });
  }, 500);

  const handleSearchSyncPartners = debounce((value: string) => {
    syncPartnerKeyword.setValue(value);
    setSyncPartners([]);
    PartnerActions.fetch_sync_partners_request({ keyword: value || '', page: 1 });
  }, 500);

  const partnerFileListValue: CloudFile[] = useMemo(() => {
    let modelNumber = 0;
    return partnerFileListCt.value?.reduce((acc: CloudFile[], curr: CloudFile) => {
      // uploadState: {0, 1}: loading, {2}: success, {3}: error
      // cloudDirectory: downlaod클릭시 링크
      const { originName } = curr;
      const type = originName.slice(originName.lastIndexOf('.') + 1);
      if (modelTypes.includes(type)) modelNumber = modelNumber + 1;
      const obj = {
        ...curr,
        type,
        isDownload: false,
        isView: false,
        viewColor:
          modelTypes.includes(type) && modelNumber <= 20 ? viewColors[modelNumber - 1] : null,
        isChecked: false,
        isWireframe: false,
        opacity: 100,
        isActive: false,
      };
      return acc.concat(obj);
    }, []);
  }, [partnerFileListCt.value]);

  const cloudFileListValue: CloudFile[] = useMemo(() => {
    let modelNumber = 0;
    return cloudFileListCt.value?.reduce((acc: CloudFile[], curr: CloudFile) => {
      // uploadState: {0, 1}: loading, {2}: success, {3}: error
      // cloudDirectory: downlaod클릭시 링크
      const { originName } = curr;
      const type = originName.slice(originName.lastIndexOf('.') + 1);
      // 사용X
      if (modelTypes.includes(type)) modelNumber = modelNumber + 1;
      const obj = {
        ...curr,
        type,
        isDownload: false,
        isView: false,
        viewColor:
          modelTypes.includes(type) && modelNumber <= 20 ? viewColors[modelNumber - 1] : null,
        isChecked: false,
        isWireframe: false,
        opacity: 100,
        isActive: false,
      };
      return acc.concat(obj);
    }, []);
  }, [cloudFileListCt.value]);

  // onChange cloueFileList: ProjectContainer 에서 cloudFileList변경에 따라 적용
  // init list after data fetch
  useEffect(() => {
    const duplicateCloudFiless = cloudFileListValue.filter(
      (files: any, idx: number, arr: any[]) => {
        const retrunValue =
          arr.findIndex((item: any) => item.originName === files.originName) !== idx;
        return retrunValue;
      },
    );
    cloudFileListValue.forEach(item => {
      item.isDisplayed = true;
    });
    if (duplicateCloudFiless.length > 0) {
      for (let i = 0; i < cloudFileListValue.length; i++) {
        for (let j = 0; j < duplicateCloudFiless.length; j++) {
          if (cloudFileListValue[i].originName === duplicateCloudFiless[j].originName) {
            if (cloudFileListValue[i].uploadState > duplicateCloudFiless[j].uploadState) {
              cloudFileListValue[i].isDisplayed = false;
            }
            break;
          }
        }
      }
    }
    cloudFileList.setValue(cloudFileListValue);
    viewModelFile.setValue([]);
    viewImageFile.setValue(null);
    viewHtmlFile.setValue(null);
  }, [cloudFileListCt.value, cloudFileListValue]);

  useEffect(() => {
    const duplicatePartnerFiless = partnerFileListValue.filter(
      (files: any, idx: number, arr: any[]) => {
        const retrunValue =
          arr.findIndex((item: any) => item.originName === files.originName) !== idx;
        return retrunValue;
      },
    );
    partnerFileListValue.forEach(item => {
      item.isDisplayed = true;
    });

    if (duplicatePartnerFiless.length > 0) {
      for (let i = 0; i < partnerFileListValue.length; i++) {
        for (let j = 0; j < duplicatePartnerFiless.length; j++) {
          if (partnerFileListValue[i].originName === duplicatePartnerFiless[j].originName) {
            if (partnerFileListValue[i].uploadState > duplicatePartnerFiless[j].uploadState) {
              partnerFileListValue[i].isDisplayed = false;
            }
            break;
          }
        }
      }
    }
    partnerFileList.setValue(partnerFileListValue);
    viewModelFile.setValue([]);
    viewImageFile.setValue(null);
    viewHtmlFile.setValue(null);
  }, [partnerFileListCt.value, partnerFileListValue]);

  // onChange localFileInput: localFileInput의 변경에 따라 Context변수 변경(상위 onUpload이벤트에서 사용)
  useDidUpdateEffect(() => {
    const newLocalFileList = updateLocalFileList(localFileInput.value);
    const exsitingFile = localFileListCt.value.find((prevItem: LocalFile) => {
      const hasSameFile = !!newLocalFileList.find((newLocalFile: LocalFile) => {
        return prevItem.name === newLocalFile.name;
      });
      return hasSameFile;
    });

    if (exsitingFile) {
      AppActions.add_popup({
        isOpen: true,
        content: <T>PROJECT_UPLOAD_FILE_OVERWRITE</T>,
        onClick() {
          inputLocalFile();
        },
      });
    } else {
      localFileListCt.setValue((draft: LocalFile[]) => draft.concat(newLocalFileList));
      if (ipcRenderer) localFileUploadIpc();
    }
  }, [localFileInput.value]);

  useEffect(() => {
    if (!ipcRenderer) return;
    ipcRenderer.on(
      'importFileRes',
      (e: any, data: { status: boolean; directoryType: string; files: any[] }) => {
        if (data?.files?.length === 0) return;

        const fileObj = data.files.reduce((acc: any[], curr: any) => {
          let modelNumber = 0;
          const type = curr.fileName.slice(curr.fileName.lastIndexOf('.') + 1);
          if (modelTypes.includes(type)) modelNumber = modelNumber + 1;

          const obj = {
            name: curr.fileName,
            path: curr.filePath,
          };

          return acc.concat(obj);
        }, []);

        localFileInput.setValue(fileObj);
      },
    );
  }, []);

  useDidUpdateEffect(() => {
    if (uploadProjectFileSuccess) {
      localFileInput.setValue([]);
      localFileInputRef.current.value = null;
    }
  }, [!!uploadProjectFileSuccess]);

  useEffect(() => {
    return () => {
      viewModelFile.setValue([]);
      viewImageFile.setValue(null);
      viewHtmlFile.setValue(null);
      cloudFileList.setValue([]);
    };
  }, []);

  useEffect(() => {
    PartnerActions.fetch_sync_partners_request({ keyword: '', page: 1 });
  }, []);

  // localFileList parsing
  const updateLocalFileList = useCallback(
    items => {
      if (localFileListCt.value?.find((item: CloudFile) => item.isView)) {
        localFileListCt.setValue(
          localFileListCt.value.reduce((acc: any[], curr: any) => {
            curr.isView = false;
            return acc.concat(curr);
          }, []),
        );
      }

      viewModelFile.setValue([]);
      viewImageFile.setValue(null);
      viewHtmlFile.setValue(null);

      let modelNumber = localFileListCt?.value?.length || 0;
      const returnValue = items?.reduce((acc: any[], curr: any, index: number) => {
        const { id, file, name } = curr;
        const type = name.slice(name.lastIndexOf('.') + 1);
        if (modelTypes.includes(type)) modelNumber = modelNumber + 1;
        const obj = {
          index,
          id: uuid(),
          file: !!file ? file : curr,
          name,
          type,
          isView: false,
          isChecked: true,
          viewColor:
            modelTypes.includes(type) && modelNumber <= 20 ? viewColors[modelNumber - 1] : null,
          isWireframe: false,
          opacity: 100,
        };

        return acc.concat(obj);
      }, []);
      return returnValue;
    },
    [localFileListCt.value],
  );
  // TODO: { originName, extension }[] 로 변경
  // 중복된 파일을 override하여 적용
  const inputLocalFile = useCallback(() => {
    localFileListCt.setValue((draft: LocalFile[]) => {
      const newLocalFileList = updateLocalFileList(localFileInput.value);
      let prevFileList = [...draft];
      let addFileNameList: any[] = [];

      for (let i = 0; i < prevFileList.length; i++) {
        for (let currentFile of newLocalFileList) {
          if (prevFileList[i].name === currentFile.name) {
            prevFileList[i] = currentFile;
            addFileNameList = [...addFileNameList, currentFile.name];
          }
        }
      }
      const addFileList = newLocalFileList.filter((item: LocalFile) => {
        return !addFileNameList.includes(item.name);
      });

      const convertedFileList = prevFileList?.length
        ? prevFileList.concat(addFileList)
        : newLocalFileList;

      return convertedFileList;
    });
    if (ipcRenderer) {
      localFileUploadIpc();
      // projectId
    }
  }, [localFileInput.value]);

  const localFileUploadIpc = () => {
    const returnValue = localFileInput.value?.reduce((acc: any[], curr: any, index: number) => {
      const extension = curr.name.substring(curr.name.lastIndexOf('.'));
      const obj = {
        fileName: curr.name,
        filePath: !!curr.path ? curr.path : curr.file.path,
        extension,
      };
      return acc.concat(obj);
    }, []);
    const submitData = {
      projectId,
      files: returnValue,
    };
    if (ipcRenderer) {
      ipcRenderer.send('projectFileLocalUpload', submitData);
      //NOTE: File upload시, File 종류에 따른 타임라인 변경
      // .stl => SCAN
      // _cad.stl, .pts => CAD
      // .stl, _cad.stl, .pts => SCAN -> CAD
      const isExistCadFile = returnValue.map((item: any) => {
        const fileSplit = item.fileName.split('_');
        const isCadStl = fileSplit.some((splitData: any) => {
          // '_'로 split후 'cad.stl'이 존재하는지 여부로 "_cad.stl" 유/무 확인
          return splitData.toLowerCase() === 'cad.stl';
        });
        const isCadPts = item.extension.toLowerCase() === '.pts';
        return !!isCadStl || !!isCadPts;
      });
      const isExsitSacnFile = returnValue.map((item: any) => {
        return scanFileExtionsion.some((extenstion: any) => {
          return extenstion === item.extension.toLowerCase();
        });
      });

      const scanFileFlag = isExsitSacnFile.some((isScanFile: any) => {
        return !!isScanFile;
      });
      const cadFileFlag = isExistCadFile.some((isCadFile: any) => {
        return !!isCadFile;
      });
      // .stl, _cad.stl, .pts 모두 존재하는 경우 둘 다 실행
      if (!!scanFileFlag) {
        // .stl => SCAN
        ProjectActions.edit_timeline_request({
          projectCode,
          stage: 2,
        });
      }
      if (!!cadFileFlag) {
        // _cad.stl, .pts => CAD
        setTimeout(() => {
          ProjectActions.edit_timeline_request({
            projectCode,
            stage: 3,
          });
          // 시점 이슈로 CAD인 경우 1초뒤 실행
        }, 1000);
      }
    }
  };

  const handleClearFileData = (id: any) => {
    partnerFileList.setValue((draft: any) => {
      return draft.reduce((acc: any[], curr: any) => {
        if (curr.cloudDataIdx === id) {
          curr.isWireframe = false;
          curr.opacity = 100;
        }
        return acc.concat(curr);
      }, []);
    });
    cloudFileList.setValue((draft: any) => {
      return draft.reduce((acc: any[], curr: any) => {
        if (curr.cloudDataIdx === id) {
          curr.isWireframe = false;
          curr.opacity = 100;
        }
        return acc.concat(curr);
      }, []);
    });

    localFileListCt.setValue((draft: any) => {
      return draft.reduce((acc: any[], curr: any) => {
        if (curr.id === id) {
          curr.isWireframe = false;
          curr.opacity = 100;
        }
        return acc.concat(curr);
      }, []);
    });
  };
  const handleClearAllFileData = () => {
    partnerFileList.setValue((draft: any) => {
      return draft.reduce((acc: any[], curr: any) => {
        curr.isWireframe = false;
        curr.opacity = 100;
        return acc.concat(curr);
      }, []);
    });
    cloudFileList.setValue((draft: any) => {
      return draft.reduce((acc: any[], curr: any) => {
        curr.isWireframe = false;
        curr.opacity = 100;
        return acc.concat(curr);
      }, []);
    });

    localFileListCt.setValue((draft: any) => {
      return draft.reduce((acc: any[], curr: any) => {
        curr.isWireframe = false;
        curr.opacity = 100;
        return acc.concat(curr);
      }, []);
    });
  };

  const handleClickWireframe = (id: number, data: any) => {
    const existData = viewModelFile.value.find((item: any) => {
      return item.id === id;
    });
    if (!existData) return;

    const { wireFrame } = data;
    partnerFileList.setValue((draft: any) => {
      return draft.reduce((acc: any[], curr: any) => {
        if (curr.cloudDataIdx === id) {
          curr.isWireframe = !wireFrame;
        }
        return acc.concat(curr);
      }, []);
    });
    cloudFileList.setValue((draft: any) => {
      return draft.reduce((acc: any[], curr: any) => {
        if (curr.cloudDataIdx === id) {
          curr.isWireframe = !wireFrame;
        }
        return acc.concat(curr);
      }, []);
    });

    localFileListCt.setValue((draft: any) => {
      return draft.reduce((acc: any[], curr: any) => {
        if (curr.id === id) {
          curr.isWireframe = !wireFrame;
        }
        return acc.concat(curr);
      }, []);
    });

    viewModelFile.value.map((item: any) => {
      // if (item.id === id && !!item.wireframe) {
      //   item.wireframe.visible = !item.isWireframe;
      //   item.isWireframe = !item.isWireframe;
      // }
      if (item.id === id) {
        if (!item.isWireframe) {
          addWireFrame(item.mesh);
        } else {
          removeWireFrame(item.mesh);
        }
        item.isWireframe = !item.isWireframe;
      }
    });
  };

  const handleChangeModelOpacity = (e: any, id: number, newValue: number | number[]) => {
    if (typeof newValue !== 'number') return;
    viewModelFile.value.map((item: any) => {
      if (item.id === id) {
        // item.material.opacity = newValue / 100;
        item.material.uniforms.opacity.value = newValue / 100;

        if (newValue === 100) {
          item.material.depthWrite = true;
        } else {
          item.material.depthWrite = false;
        }
      }
    });

    partnerFileList.setValue((draft: any) => {
      return draft.reduce((acc: any[], curr: any) => {
        if (curr.cloudDataIdx === id) {
          curr.opacity = newValue;
        }
        return acc.concat(curr);
      }, []);
    });

    cloudFileList.setValue((draft: any) => {
      return draft.reduce((acc: any[], curr: any) => {
        if (curr.cloudDataIdx === id) {
          curr.opacity = newValue;
        }
        return acc.concat(curr);
      }, []);
    });

    localFileListCt.setValue((draft: any) => {
      return draft.reduce((acc: any[], curr: any) => {
        if (curr.id === id) {
          curr.opacity = newValue;
        }
        return acc.concat(curr);
      }, []);
    });
  };

  const handleClickViewModelFile = useCallback(
    async (e: any, id: number, data: any) => {
      setSelectedModelFile(id);
      const { shareType, value } = data;
      const { name, type, isView, viewColor, resourceFileList = [] } = value;
      let fileUrl = '';
      const resourceFileListValue = cloudFileList.value.filter((item: any) =>
        resourceFileList.includes(item.cloudDataIdx),
      );
      const currentCloudFileList = shareType === 'partner' ? partnerFileList : cloudFileList;
      const otherCloudFileList = shareType === 'partner' ? cloudFileList : partnerFileList;

      currentCloudFileList.setValue((draft: any) => {
        // Model File 클릭 시, 먼저 선택되어있던 이미지, HTML 파일의 isView 초기화
        return draft.reduce((acc: any[], curr: any) => {
          if ([...imgTypes, 'html'].includes(curr.type)) {
            curr.isView = false;
          }
          return acc.concat(curr);
        }, []);
      });
      otherCloudFileList.setValue((draft: any) => {
        // Model File 클릭 시, 먼저 선택되어있던 이미지, HTML 파일의 isView 초기화
        return draft.reduce((acc: any[], curr: any) => {
          if ([...imgTypes, 'html'].includes(curr.type)) {
            curr.isView = false;
          }
          return acc.concat(curr);
        }, []);
      });

      localFileListCt.setValue((draft: any) => {
        // Model File 클릭 시, 먼저 선택되어있던 이미지, HTML 파일의 isView 초기화
        return draft.reduce((acc: any[], curr: any) => {
          if ([...imgTypes, 'html'].includes(curr.type)) {
            curr.isView = false;
          }
          return acc.concat(curr);
        }, []);
      });

      currentCloudFileList.setValue((draft: any) => {
        return draft.reduce((acc: any[], curr: any) => {
          if (curr.cloudDataIdx === id) {
            curr.isView = !isView;
          }
          return acc.concat(curr);
        }, []);
      });

      otherCloudFileList.setValue((draft: any) => {
        return draft.reduce((acc: any[], curr: any) => {
          if (curr.cloudDataIdx === id) {
            curr.isView = !isView;
          }
          return acc.concat(curr);
        }, []);
      });

      // currentCloudFileList.value.map((item: any) => {
      //   if (item.cloudDataIdx === id) {
      //     item.isView = !isView;
      //   }
      // });
      // otherCloudFileList.value.map((item: any) => {
      //   if (item.cloudDataIdx === id) {
      //     item.isView = !isView;
      //   }
      // });

      checkVisibleModelFile.onChange({ value: id });
      // TODO: viewModelFile에 존재하는지 확인
      const isViewModelFile = viewModelFile.value.find((item: any) => item.id === id);

      if (!!isViewModelFile) {
        viewModelFile.setValue((draft: any) => {
          if (draft.some((item: any) => item.id === id)) {
            return draft.filter((item: any) => item.id !== id);
          }
        });
        handleClearFileData(id);
        // viewModelFile.setValue((draft: any) => {
        //   // Model File 클릭 시, 먼저 선택되어있던 이미지, HTML 파일의 isView 초기화
        //   return draft.reduce((acc: any[], curr: any) => {
        //     if (curr.id === id) {
        //       curr.material.visible = !isView;
        //       curr.isView = !isView;
        //     }
        //     return acc.concat(curr);
        //   }, []);
        // });
      } else {
        isModelFileLoadingProgress.setValue(true);
        // 파일 처음 클릭 시
        try {
          const { data } = await fetchProjectFileDownloadUrl({ cloudDataIdx: id });
          const { signedUrl } = data;

          if (!signedUrl) return;

          fileUrl = signedUrl;

          // const { data: fileData } = await axs({
          //   url: signedUrl,
          //   method: 'get',
          //   responseType: 'blob',
          // });

          // if (!fileData) return;

          // const file = await getFileDataUrl(fileData);
          // if (type === 'obj') {
          //   fileUrl = fileData;
          // } else {
          //   fileUrl = file;
          // }
        } catch (error) {
          AppActions.show_toast({
            type: 'error',
            message: (error as { message: string })?.message,
          });
        }

        const mtlCloudIdx = resourceFileListValue.find(
          (item: any) => item.type === 'mtl',
        )?.cloudDataIdx;
        let mtlUrl = '';
        if (!!mtlCloudIdx) {
          try {
            const { data } = await fetchProjectFileDownloadUrl({ cloudDataIdx: mtlCloudIdx });
            const { signedUrl } = data;

            if (!signedUrl) return;

            mtlUrl = signedUrl;

            // const { data: fileData } = await axs({
            //   url: signedUrl,
            //   method: 'get',
            //   responseType: 'blob',
            // });
            // if (!fileData) return;
            // mtlUrl = fileData;
          } catch (error) {
            AppActions.show_toast({
              type: 'error',
              message: (error as { message: string })?.message,
            });
          }
        }

        let textureUrl = '';
        const textureCloudIdx = resourceFileListValue.find((item: any) => {
          return [...imgTypes].includes(item.type);
        })?.cloudDataIdx;

        if (!!textureCloudIdx) {
          try {
            const { data } = await fetchProjectFileDownloadUrl({ cloudDataIdx: textureCloudIdx });
            const { signedUrl } = data;

            if (!signedUrl) return;

            textureUrl = signedUrl;

            // const { data: fileData } = await axs({
            //   url: signedUrl,
            //   method: 'get',
            //   responseType: 'blob',
            // });
            // if (!fileData) return;
            // textureUrl = fileData;
          } catch (error) {
            AppActions.show_toast({
              type: 'error',
              message: (error as { message: string })?.message,
            });
          }
        }

        const modelData = {
          shareType,
          isView: true,
          viewColor,
          isValidObj: true,
          name,
          url: fileUrl,
          id,
          textureUrl: !!textureUrl ? textureUrl : '',
          mtlUrl: !!mtlUrl ? mtlUrl : '',
          isLocal: false,
          isWireframe: false,
          opacity: 100,
          material: undefined,
          wireframe: undefined,
        };
        viewModelFile.setValue((draft: any) => {
          // return draft.concat(modelData);
          if (!draft.length) {
            return draft.concat(modelData);
          } else {
            // re click 경우
            if (draft.some((item: any) => item.id === id)) {
              return draft.filter((item: any) => item.id !== id);
            } else {
              return draft.concat(modelData);
            }
          }
        });
      }
      viewHtmlFile.setValue(null);
      viewImageFile.setValue(null);
      return true;
    },
    [viewModelFile.value, cloudFileList.value, localFileListCt.value, partnerFileList.value],
  );

  const handleClickViewModelFileLocal = useCallback(
    async (e: any, id: string, data: any) => {
      setSelectedModelFile(id);
      const { value } = data;

      const { name, type, isView, viewColor, resourceFileList = [] } = value;
      let filePath: string = '';
      let mtlPath: string = '';
      let texturePath: string = '';

      let modelFileData: any = '';
      let mtlFileData: any = '';
      let textureFileData: any = '';

      let fileUrl = '';
      const resourceFileListValue = cloudFileList.value.filter((item: any) =>
        resourceFileList.includes(item.cloudDataIdx),
      );

      partnerFileList.setValue((draft: any) => {
        // Model File 클릭 시, 먼저 선택되어있던 이미지, HTML 파일의 isView 초기화
        return draft.reduce((acc: any[], curr: any) => {
          if ([...imgTypes, 'html'].includes(curr.type)) {
            curr.isView = false;
          }
          return acc.concat(curr);
        }, []);
      });
      cloudFileList.setValue((draft: any) => {
        // Model File 클릭 시, 먼저 선택되어있던 이미지, HTML 파일의 isView 초기화
        return draft.reduce((acc: any[], curr: any) => {
          if ([...imgTypes, 'html'].includes(curr.type)) {
            curr.isView = false;
          }
          return acc.concat(curr);
        }, []);
      });

      localFileListCt.setValue((draft: any) => {
        // Model File 클릭 시, 먼저 선택되어있던 이미지, HTML 파일의 isView 초기화
        return draft.reduce((acc: any[], curr: any) => {
          if ([...imgTypes, 'html'].includes(curr.type)) {
            curr.isView = false;
          }
          return acc.concat(curr);
        }, []);
      });

      localFileListCt.value.map((item: any) => {
        if (item.id === id) {
          item.isView = !isView;
        }
      });

      checkVisibleModelFile.onChange({ value: id });
      // TODO: viewModelFile에 존재하는지 확인
      const isViewModelFile = viewModelFile.value.find((item: any) => item.id === id);

      if (!!isViewModelFile) {
        viewModelFile.setValue((draft: any) => {
          if (draft.some((item: any) => item.id === id)) {
            return draft.filter((item: any) => item.id !== id);
          }
        });
        handleClearFileData(id);

        // viewModelFile.setValue((draft: any) => {
        //   // Model File 클릭 시, 먼저 선택되어있던 이미지, HTML 파일의 isView 초기화
        //   return draft.reduce((acc: any[], curr: any) => {
        //     if (curr.id === id) {
        //       curr.material.visible = !isView;
        //       curr.isView = !isView;
        //     }
        //     return acc.concat(curr);
        //   }, []);
        // });
      } else {
        isModelFileLoadingProgress.setValue(true);
        // 파일 처음 클릭 시
        if (!ipcRenderer) return;
        const fileName = name.split('.')[0];
        localFileListCt.value.find((item: any) => {
          if (item.name.split('.')[0] === fileName) {
            if (item.type === type) {
              filePath = item.name;
            }
            if (item.type === 'mtl') {
              mtlPath = item.name;
            }
            if ([...imgTypes].includes(item.type)) {
              texturePath = item.name;
            }
          }
        });

        if (!!name) {
          try {
            const modelFileLoad = await ipcRenderer.invoke('projectFileLocalLoadAsync', {
              projectId,
              fileName: name,
            });

            modelFileData = modelFileLoad.fileUrl;
          } catch (error) {
            AppActions.show_toast({
              type: 'error',
              message: (error as { message: string })?.message,
            });
          }
        }

        if (!!mtlPath) {
          try {
            const mtlFileLoad = await ipcRenderer.invoke('projectFileLocalLoadAsync', {
              projectId,
              fileName: mtlPath,
            });
            mtlFileData = mtlFileLoad.fileUrl;
            // const fileBlob = new Blob([mtlFileLoad?.fileData], {
            //   type: 'application/x-www-form-urlencoded',
            // });

            // const fileUrl = URL.createObjectURL(fileBlob);
            // mtlFileData = fileUrl;
          } catch (error) {
            AppActions.show_toast({
              type: 'error',
              message: (error as { message: string })?.message,
            });
          }
        }
        if (!!texturePath) {
          try {
            const textureFileLoad = await ipcRenderer.invoke('projectFileLocalLoadAsync', {
              projectId,
              fileName: texturePath,
            });
            textureFileData = textureFileLoad.fileUrl;
            // const fileBlob = new Blob([textureFileLoad?.fileData], {
            //   type: 'application/x-www-form-urlencoded',
            // });

            // const fileUrl = URL.createObjectURL(fileBlob);
            // textureFileData = fileUrl;
          } catch (error) {
            AppActions.show_toast({
              type: 'error',
              message: (error as { message: string })?.message,
            });
          }
        }

        const modelData = {
          isView: !isView,
          viewColor,
          isValidObj: true,
          name,
          url: modelFileData,
          id,
          mtlUrl: !!mtlFileData ? mtlFileData : '',
          textureUrl: !!textureFileData ? textureFileData : '',
          isLocal: true,
          isWireframe: false,
          opacity: 100,
          material: undefined,
          wireframe: undefined,
        };

        viewModelFile.setValue((draft: any) => {
          if (!draft.length) {
            return draft.concat(modelData);
          } else {
            // re click 경우
            if (draft.some((item: any) => item.id === id)) {
              return draft.filter((item: any) => item.id !== id);
            } else {
              return draft.concat(modelData);
            }
          }
        });
      }
      viewHtmlFile.setValue(null);
      viewImageFile.setValue(null);
      return;
    },
    [viewModelFile.value, cloudFileList.value, localFileListCt.value, partnerFileList.value],
  );

  const handleClickImageFile = async (e: any, id: number, data: any) => {
    const { action, shareType, value } = data;
    const { isView } = value;

    const currentCloudFileList = shareType === 'partner' ? partnerFileList : cloudFileList;
    const otherCloudFileList = shareType === 'partner' ? cloudFileList : partnerFileList;

    currentCloudFileList.setValue((draft: any) => {
      return draft.reduce((acc: any[], curr: any) => {
        if (curr.cloudDataIdx === id) {
          curr.isView = !isView;
        } else {
          curr.isView = false;
        }
        return acc.concat(curr);
      }, []);
    });
    otherCloudFileList.setValue((draft: any) => {
      return draft.reduce((acc: any[], curr: any) => {
        if (curr.cloudDataIdx === id) {
          curr.isView = !isView;
        } else {
          curr.isView = false;
        }
        return acc.concat(curr);
      }, []);
    });
    localFileListCt.setValue((draft: any) => {
      return draft.reduce((acc: any[], curr: any) => {
        curr.isView = false;
        return acc.concat(curr);
      }, []);
    });

    // TODO: viewModelFile에 존재하는지 확인
    if (viewModelFile.value.length > 0) {
      viewModelFile.setValue([]);
      handleClearAllFileData();
      checkVisibleModelFile.setValue(new Set());
      // viewModelFile.setValue((draft: any) => {
      //   // Model File 클릭 시, 먼저 선택되어있던 이미지, HTML 파일의 isView 초기화
      //   return draft.reduce((acc: any[], curr: any) => {
      //     curr.material.visible = false;
      //     return acc.concat(curr);
      //   }, []);
      // });
    }

    if (!!isView) {
      viewImageFile.setValue(null);
      viewHtmlFile.setValue(null);
    } else {
      try {
        const { data } = await fetchProjectFileDownloadUrl({ cloudDataIdx: id });
        const { signedUrl } = data;

        if (!signedUrl) return;

        const { data: fileData } = await axs({
          url: signedUrl,
          method: 'get',
          responseType: 'blob',
        });

        if (!fileData) return;

        if (action === 'html') {
          const file = new File([fileData], '', {
            type: 'text/html',
          });
          if (!file) return;
          const htmlUrl = URL.createObjectURL(file);
          const iframe = htmlViewerRef.current as HTMLIFrameElement;
          if (!iframe) return;
          iframe.src = htmlUrl;
          viewHtmlFile.setValue(true);
          viewImageFile.setValue(null);
        } else {
          const file = await getFileDataUrl(fileData);
          viewImageFile.setValue(file);
          viewHtmlFile.setValue(null);
        }
      } catch (error) {
        AppActions.show_toast({
          type: 'error',
          message: (error as { message: string })?.message,
        });
      }
    }

    return;
  };

  const handleClickImageFileLocal = async (e: any, id: number, data: any) => {
    const { action, value } = data;
    const { isView, name, type } = value;

    partnerFileList.setValue((draft: any) => {
      return draft.reduce((acc: any[], curr: any) => {
        curr.isView = false;
        return acc.concat(curr);
      }, []);
    });
    cloudFileList.setValue((draft: any) => {
      return draft.reduce((acc: any[], curr: any) => {
        curr.isView = false;
        return acc.concat(curr);
      }, []);
    });
    localFileListCt.setValue((draft: any) => {
      return draft.reduce((acc: any[], curr: any) => {
        if (curr.id === id) {
          curr.isView = !isView;
        } else {
          curr.isView = false;
        }
        return acc.concat(curr);
      }, []);
    });

    // TODO: viewModelFile에 존재하는지 확인
    if (viewModelFile.value.length > 0) {
      viewModelFile.setValue([]);
      handleClearAllFileData();
      checkVisibleModelFile.setValue(new Set());
      // viewModelFile.setValue((draft: any) => {
      //   // Model File 클릭 시, 먼저 선택되어있던 이미지, HTML 파일의 isView 초기화
      //   return draft.reduce((acc: any[], curr: any) => {
      //     curr.material.visible = false;
      //     return acc.concat(curr);
      //   }, []);
      // });
    }

    if (!!isView) {
      viewImageFile.setValue(null);
      viewHtmlFile.setValue(null);
    } else {
      if (!ipcRenderer) return;

      if (action === 'html') {
        const localFileLoad = await ipcRenderer.invoke('projectFileLocalLoadAsync', {
          projectId,
          fileName: name,
        });
        const fileBlob = new Blob([new Uint8Array(localFileLoad?.fileData)], {
          type: 'text/html',
        });
        const fileData = new File([fileBlob], name, {
          type: 'text/html',
        });
        if (!fileData) return;
        const iframe = htmlViewerRef.current as HTMLIFrameElement;
        if (!iframe) return;
        const htmlUrl = URL.createObjectURL(fileData);
        iframe.src = htmlUrl;
        viewHtmlFile.setValue(true);
        viewImageFile.setValue(null);
      } else {
        ipcRenderer.send('projectFileLocalLoad', {
          projectId,
          fileName: name,
        });

        ipcRenderer.once('projectFileLocalLoadRes', async (e: any, data: any) => {
          const fileBlob = new Blob([new Uint8Array(data?.fileData)], { type });
          const file = new File([fileBlob], name, {
            type: type,
          });

          const dataUrl = await getFileDataUrl(file);
          viewImageFile.setValue(dataUrl);
          viewHtmlFile.setValue(null);
        });
      }
    }

    return;
  };

  const handleSyncProject = () => {
    const checkedFileIdxList = cloudFileList.value.reduce((acc: any, curr: CloudFile) => {
      if (curr.isChecked) {
        return acc.concat(curr.cloudDataIdx);
      }
      return acc;
    }, []);
    const dataIdxListToShare = checkedFileIdxList.filter((checkedItem: number) => {
      return !partnerFileList.value.find((partnerItem: CloudFile) => {
        return checkedItem === partnerItem.cloudDataIdx;
      });
    });
    // 파일선택이 없을 경우, cloudDataList 빈배열로 api만 호출
    if (!localFileToUpload?.length) {
      if (dataIdxListToShare.length) {
        // 시나리오상 없는 경우
        // group에 먼저 업로드 후 partner로 이동
        console.log('HDS] 111111111111111111 ??');
        FileActions.edit_project_share_request({
          projectCode,
          cloudDataList: dataIdxListToShare,
          isShare: 2,
        });
      } else {
        // 업로드할 파일 없이 sync만 보내기
        ProjectActions.create_project_sync_request({
          projectCode,
          partnerGroupIdx: selectedSyncPartner.value,
          cloudDataList: [],
        });
      }
    } else {
      // 파일 포함
      if (!!selectedSyncPartner.value && !syncInfo) {
        onCheckExistingUploadUserFile(() => onUploadLocalFile(1, selectedSyncPartner.value));
      }
      if (!!syncInfo) {
        onCheckExistingUploadUserFile(() => onUploadLocalFile(2), 2);
      }
    }
  };

  const handleUploadToPartner = useCallback(() => {
    console.log('HDS] handleUploadToPartner _______ ');
    const checkedFileIdxList = cloudFileList.value.reduce((acc: any, curr: CloudFile) => {
      if (curr.isChecked) {
        return acc.concat(curr.cloudDataIdx);
      }
      return acc;
    }, []);
    const dataIdxListToShare = checkedFileIdxList.filter((checkedItem: number) => {
      return !partnerFileList.value.find((partnerItem: CloudFile) => {
        return checkedItem === partnerItem.cloudDataIdx;
      });
    });
    console.log('HDS] cloudFileList ___________ ', cloudFileList);
    console.log('HDS] checkedFileIdxList ___________ ', checkedFileIdxList);
    console.log('HDS] dataIdxListToShare ___________ ', dataIdxListToShare);
    if (localFileToUpload?.length) {
      console.log('HDS] localFileToUpload ___________ ', localFileToUpload);

      onCheckExistingUploadUserFile(() => onUploadLocalFile(2), 2);
    } else if (dataIdxListToShare.length) {
      FileActions.edit_project_share_request({
        projectCode,
        cloudDataList: dataIdxListToShare,
        isShare: 2,
      });
    }
  }, [cloudFileList.value, localFileToUpload]);
  const handleUploadToGroup = () => {
    //Partner File List에서 체크한건지 확인
    const checkedFileIdxList = partnerFileList.value.reduce((acc: any, curr: CloudFile) => {
      if (curr.isChecked) {
        return acc.concat(curr.cloudDataIdx);
      }
      return acc;
    }, []);

    if (localFileToUpload?.length) {
      onCheckExistingUploadUserFile(() => onUploadLocalFile(1));
    } else if (checkedFileIdxList.length) {
      // partner공유에서 제거
      console.log('HDS] handleUploadToGroup __________ ', checkedFileIdxList);
      FileActions.edit_project_share_request({
        projectCode,
        cloudDataList: checkedFileIdxList,
        isShare: 1,
      });
    }
  };

  const handleClickCheckbox = (e: any, id: number, storage: string, data: any) => {
    const { shareType, isChecked } = data;
    if (storage === 'cloud') {
      const currentCloudFileList = shareType === 'partner' ? partnerFileList : cloudFileList;
      const otherCloudFileList = shareType === 'partner' ? cloudFileList : partnerFileList;

      currentCloudFileList.setValue((draft: any) => {
        return draft.reduce((acc: any[], curr: any) => {
          if (curr.cloudDataIdx === id) {
            curr.isChecked = !curr.isChecked;
          }
          return acc.concat(curr);
        }, []);
      });
      otherCloudFileList.setValue((draft: any) => {
        return draft.reduce((acc: any[], curr: any) => {
          curr.isChecked = false;
          return acc.concat(curr);
        }, []);
      });

      localFileListCt.setValue((draft: any) => {
        return draft.reduce((acc: any[], curr: any) => {
          curr.isChecked = false;
          return acc.concat(curr);
        }, []);
      });
    } else {
      partnerFileList.setValue((draft: any) => {
        return draft.reduce((acc: any[], curr: any) => {
          curr.isChecked = false;
          return acc.concat(curr);
        }, []);
      });

      cloudFileList.setValue((draft: any) => {
        return draft.reduce((acc: any[], curr: any) => {
          curr.isChecked = false;
          return acc.concat(curr);
        }, []);
      });

      localFileListCt.setValue((draft: any) => {
        return draft.reduce((acc: any[], curr: any) => {
          if (curr.id === id) {
            curr.isChecked = !curr.isChecked;
          }
          return acc.concat(curr);
        }, []);
      });
    }
  };

  const handleClickCheckboxAll = (
    e: any,
    storage: string,
    isCheckAll: boolean,
    shareType: string,
  ) => {
    if (storage === 'cloud') {
      const currentCloudFileList = shareType === 'partner' ? partnerFileList : cloudFileList;
      const otherCloudFileList = shareType === 'partner' ? cloudFileList : partnerFileList;
      currentCloudFileList.setValue((draft: any) => {
        return draft.reduce((acc: any[], curr: any) => {
          if (curr.isOwner) {
            curr.isChecked = !isCheckAll;
          }
          return acc.concat(curr);
        }, []);
      });

      otherCloudFileList.setValue((draft: any) => {
        return draft.reduce((acc: any[], curr: any) => {
          curr.isChecked = false;
          return acc.concat(curr);
        }, []);
      });

      localFileListCt.setValue((draft: any) => {
        return draft.reduce((acc: any[], curr: any) => {
          curr.isChecked = false;
          return acc.concat(curr);
        }, []);
      });
    } else {
      partnerFileList.setValue((draft: any) => {
        return draft.reduce((acc: any[], curr: any) => {
          curr.isChecked = false;
          return acc.concat(curr);
        }, []);
      });

      cloudFileList.setValue((draft: any) => {
        return draft.reduce((acc: any[], curr: any) => {
          curr.isChecked = false;
          return acc.concat(curr);
        }, []);
      });

      localFileListCt.setValue((draft: any) => {
        return draft.reduce((acc: any[], curr: any) => {
          curr.isChecked = !isCheckAll;
          return acc.concat(curr);
        }, []);
      });
    }
  };

  const handleClickDeleteFiles = (storage: string, deletelist: any[]) => {
    const deleteIdData = deletelist.map((item: any) => {
      return item.cloudDataIdx;
    });
    if (storage === 'partner') {
      FileActions.edit_project_share_request({
        projectCode,
        cloudDataList: deleteIdData,
        isShare: 1,
      });
    } else if (storage === 'group') {
      let name = '';
      deletelist.forEach((item, idx) => {
        if (idx === 0) {
          name += `${item.originName}`;
        } else {
          name += `, ${item.originName}`;
        }
      });
      const submitData = {
        projectCode,
        cloudFileList: deleteIdData,
      };
      FileActions.delete_project_file_request(submitData);

      const createHistoryData = {
        projectCode,
        type: projectEventType.fileDelete,
        params: {
          content: `${company}, ${name}`,
        },
      };
      ProjectActions.create_project_history_request(createHistoryData);
    } else {
      if (ipcRenderer) {
        deletelist.map(item => {
          ipcRenderer.send('projectFileLocalDelete', {
            projectId,
            fileName: item,
          });
        });

        ipcRenderer.on('projectFileLocalDeleteRes', (e: any, data: any) => {
          if (data.status) {
            localFileListCt.setValue((draft: any) =>
              draft.filter((item: any) => item.name !== data.fileName),
            );
          } else {
            // TODO: file delete 실패 알림
          }
        });
      }
    }
  };

  const handleClickDownloadFiles = async (id: number, name: string) => {
    try {
      const { data } = await fetchProjectFileDownloadUrl({ cloudDataIdx: id });
      const { signedUrl } = data;
      // NOTE: Electron download
      if (ipcRenderer) {
        downloadFileWithElectron({
          name,
          url: signedUrl,
          properties: {
            projectCode,
            projectId,
            fileName: name,
          },
          type: 'download',
          callback: onLoadLocalFiles,
        });
      } else {
        downloadFile({
          url: signedUrl,
          name,

          success() {
            cloudFileList.setValue((draft: any) => {
              return draft.reduce((acc: any[], curr: any) => {
                if (curr.cloudDataIdx === id) {
                  curr.isDownload = true;
                }

                return acc.concat(curr);
              }, []);
            });
          },
        });
      }

      const createHistoryData = {
        projectCode,
        type: projectEventType.fileDownload,
        params: {
          content: `${company}, ${name}`,
        },
      };
      ProjectActions.create_project_history_request(createHistoryData);
    } catch (error) {
      AppActions.show_toast({
        type: 'error',
        message: (error as { message: string })?.message,
      });
    }
  };

  // NOTE: file Drag&Drop
  const handleAddLocalFiles = (data: any) => {
    const { dropFiles } = data;
    if (ipcRenderer) {
      localFileInput.setValue(dropFiles);
    } else {
      localFileInput.setValue(dropFiles);
      if (cloudFileList.value?.find((item: CloudFile) => item.isChecked)) {
        cloudFileList.setValue(
          cloudFileList.value.reduce((acc: any[], curr: any) => {
            curr.isChecked = false;
            return acc.concat(curr);
          }, []),
        );
      }
      if (partnerFileList.value?.find((item: CloudFile) => item.isChecked)) {
        partnerFileList.setValue(
          partnerFileList.value.reduce((acc: any[], curr: any) => {
            curr.isChecked = false;
            return acc.concat(curr);
          }, []),
        );
      }
    }
  };

  const handleClickFileImport = () => {
    if (!ipcRenderer) return;
    ipcRenderer.send('importFile', {
      directoryType: 'SCAN_WORKSPACE',
    });
  };

  const handleClearView = () => {
    viewModelFile.setValue([]);
    cloudFileList.setValue(
      cloudFileList.value.reduce((acc: any[], curr: any) => {
        curr.isView = false;
        return acc.concat(curr);
      }, []),
    );
    partnerFileList.setValue(
      partnerFileList.value.reduce((acc: any[], curr: any) => {
        curr.isView = false;
        return acc.concat(curr);
      }, []),
    );
  };

  const handleClickWebViewer = () => {
    if (scanFileListCt.value.length > 0) {
      let isWebViewOpen = false;
      scanFileListCt.value.map((file: any) => {
        if (scanfileTypes.includes(file.extension)) {
          isWebViewOpen = scanfileTypes.includes(file.extension);
        }
      });
      if (!isWebViewOpen) return;
      // ScanFile 존재
      const viewUrl = `${process.env.REACT_APP_WEB_VIEW_URL}/${projectCode}`;
      ipcRenderer.send('openBrowser', {
        url: viewUrl,
      });
    } else {
      // ScanFile 없음, 웹뷰어 사용불가
      AppActions.add_popup({
        isOpen: true,
        isCloseIcon: true,
        width: 660,
        title: '',
        content: <T>OPEN_FAIL_WEB_VIEW</T>,
        onClick() {
          return;
        },
        onCancel() {
          return;
        },
      });
    }
  };

  useDidUpdateEffect(() => {
    // NOTE: PMS ViewThumbnail
    if (!isPmsOrder) return;
    if (pmsCommand !== pmsViewThumbnail) return;
    if (!isPmsReady) return;
    if (cloudFileList.value.length > 0) {
      let lastIndex = 0;
      cloudFileList.value.map(async (item: any) => {
        lastIndex++;
        if (item.isScan !== 1) return;
        if (!scanfileTypes.includes(item.extension)) return;
        await handleClickViewModelFile(_, item.cloudDataIdx, {
          action: 'toggleViewCloudModelFile',
          shareType: 'group',
          value: {
            name: item.originName,
            type: item.type,
            isView: item.isView,
            viewColor: item.viewColor,
            resourceFileList: item.resourceFileList,
            groupName: item.groupName,
          },
        });
      });
      if (lastIndex === cloudFileList.value.length) {
        console.log('PMS] handleClearPmsData Ready 1111111111');
        handleClearPmsData();
      }
    } else {
      console.log('PMS] handleClearPmsData Ready 22222222');
      handleClearPmsData();
    }
  }, [cloudFileList.value, isPmsReady, isPmsOrder, pmsCommand, pmsViewThumbnail]);

  return (
    <ProjectFile
      isRequestedSync={isRequestedSync}
      isCheckedCloud={isCheckedCloud}
      isCheckedLocal={isCheckedLocal}
      projectInfo={projectInfo}
      isSelectedPartner={isSelectedPartner}
      hasAlarm={hasAlarm}
      hasViewer={hasViewer}
      localFileInput={localFileInput}
      localFileInputRef={localFileInputRef}
      localFileListCt={localFileListCt}
      partnerFileList={partnerFileList}
      cloudFileList={cloudFileList}
      viewModelFile={viewModelFile}
      htmlViewerRef={htmlViewerRef}
      viewHtmlFile={viewHtmlFile}
      viewImageFile={viewImageFile}
      onAddLocalFiles={handleAddLocalFiles}
      onClickFileImport={handleClickFileImport}
      onClearView={handleClearView}
      syncPartners={syncPartners}
      hasMoreSyncPartners={hasMoreSyncPartners}
      selectedSyncPartner={selectedSyncPartner}
      onFetchSyncPartners={handleFetchSyncPartners}
      onSearchSyncPartners={handleSearchSyncPartners}
      onClickViewModelFile={handleClickViewModelFile}
      onClickViewModelFileLocal={handleClickViewModelFileLocal}
      onClickImageFile={handleClickImageFile}
      onClickImageFileLocal={handleClickImageFileLocal}
      onClickWireframe={handleClickWireframe}
      onChangeModelOpacity={handleChangeModelOpacity}
      checkVisibleModelFile={checkVisibleModelFile.value}
      isModelFileLoadingProgress={isModelFileLoadingProgress}
      selectedModelFile={selectedModelFile}
      onClickCheckbox={handleClickCheckbox}
      onClickCheckboxAll={handleClickCheckboxAll}
      onClickDeleteFiles={handleClickDeleteFiles}
      onClickDownloadFiles={handleClickDownloadFiles}
      onSyncProject={handleSyncProject}
      onUploadToPartner={handleUploadToPartner}
      onUploadToGroup={handleUploadToGroup}
      onClickWebViewer={handleClickWebViewer}
    />
  );
}

export default ProjectFileContainer;
